/**
 * SbDrawer.jsx
 *
 * SB-branded drawer component.
 * Pattern mirrors cfDrawer from cf-webapp-2.0-master, adapted for JavaScript.
 *
 * Key differences from cfDrawer:
 *  - No Zustand store — visibility is controlled via `open` + `onClose` props
 *    (the parent owns the open/close state, same as the existing SbJdCreationDrawer)
 *  - Uses SbButton for the default close icon
 *  - Full DrawerProps passthrough via ...rest
 *
 * ─── Props ────────────────────────────────────────────────────────────────────
 *
 *   open        bool              — controls visibility (required)
 *   onClose     (e) => void       — called when the drawer is closed
 *   title       string | node     — header title
 *   width       string | number   — default '50vw'
 *   placement   string            — default 'right'
 *   footer      node              — pinned footer content (optional)
 *   closeIcon   node              — override the default close button (optional)
 *   children    node              — drawer body content
 *   ...rest     DrawerProps       — any other antd Drawer props
 */

import React, { useCallback, useMemo } from 'react';
import { CloseOutlined } from '@ant-design/icons';
import SbButton from 'uiCore/SbButton/SbButton';
import { SbDrawerStyled } from './SbDrawer.styles';

const SbDrawer = ({
  open,
  onClose,
  title,
  width = '50vw',
  placement = 'right',
  footer,
  closeIcon,
  children,
  ...rest
}) => {
  const handleClose = useCallback(
    (event) => {
      onClose?.(event);
    },
    [onClose],
  );

  return (
    <SbDrawerStyled
      open={open}
      onClose={handleClose}
      title={title}
      width={width}
      placement={placement}
      footer={footer}
      {...rest}
    >
      {children}
    </SbDrawerStyled>
  );
};

export default SbDrawer;
